/**
 * Orders
 */

// React
import React, {
  useEffect,
  useState,
  useContext,
  FC,
} from 'react';
import { useForm } from 'react-hook-form';

// Context
import AuthContext, { AuthConsumer } from '../../authContext';

// UI
import theme from '../../theme';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Grid,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiAccountOutline,
  mdiPhoneOutline,
  mdiPackageVariant,
  mdiStateMachine,
  mdiCalendar,
  mdiClose,
  mdiPencilOutline,
  mdiClock
} from '@mdi/js';

// Components
import RenderFilter from '../../utils/listDaynamicFilterHookForm';
import type { Filter } from '../../utils/listDaynamicFilter';
import List from './warehouseOperatorList';
import Pagination from '@material-ui/lab/Pagination';
import Can from '../../components/Can';
import CSV from './csv';

// Helper
import {
  removeEmptyValueObject,
  translate,
  checkMatchMedia,
} from '../../utils/helpers';

// Type
import {
  Props,
  Status,
} from './interfaces';
import ordersRepository from 'client-sdk/src/Repository/orders-repository';
import actionRepository from 'client-sdk/src/Repository/action-repository';
import handleError from '../../utils/handleError';
import handleNotfication from '../../utils/handleNotification';
import useQuery from '../../utils/hook/useQuery';
import windowSize from '../../utils/hook/useWindowResize';
import inventoryItemsRepository from 'client-sdk/src/Repository/inventory-items-repository';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      padding: '10px 0',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },
    input: {
      display: 'flex',
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 8,
      marginRight: '10px',
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    textArea: {
      width: '100%',
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      marginTop: '30px',
      top: '47px',
      right: 0,
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),

      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    secondDetail: {
      backgroundColor: 'white',
      padding: '5px',
      borderRadius: '5px',
      // width: '100%',
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'justify',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    btnBox: {
      display: 'flex',
      flexDirection: 'row-reverse',
      width: '100%',
      '@media (max-width: 768px)': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      },
    },
  })
);

type Inputs = {
  'customerNationalCode.contains': string;
};
const userId = localStorage.getItem("userId");
const Orders: FC<Props> = (props: Props): React.ReactElement => {
  const { register, handleSubmit, reset } = useForm<Inputs>(
    {
      defaultValues: {
        idType: '',
      },
    }
  );
  const [filters, setFilters] = useState([] as Filter[]);
  const { filtersValues, setFiltersValues } = useQuery({
    page: 0,
    size: 20,
    'status.in': 'ASSIGNED, PICKED, PACKAGED',
    'warehouseType.equals': 'SINGLE',
    'enableGetChildren.equals': true,
  } as any, null);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [openConfirmCancel, setOpenConfirmCancel] = useState(false as boolean);
  const [openConfirmUpdateState, setOpenConfirmUpdateState] = useState(
    false as boolean
  );

  const [id, setId] = useState(-1 as number);
  const [previewState, setPreviewState] = useState('CANCELLED' as Status);
  const [cancelDescription, setCancelDescription] = useState('' as string);
  const [lastUpdate, setLastUpdate] = useState(
    '2019-07-02T17:15:03+04:30' as string
  );
  const [count, setCount] = useState(0);
  const [ordersList, setResponse] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const authContext = useContext(AuthContext);
  const [isMob, setIsMob] = useState(false);
  const { width } = windowSize();
  const filterTranslator = (fieldName: string) => {
    const status = {
      customerName: 'نام سفارش‌دهنده:',
      'customerName.equals': 'نام سفارش‌دهنده:',
      'customerName.contais': 'نام سفارش‌دهنده:',
      centerName: 'انبار:',
      'centerName.equals': 'انبار:',
      'centerName.contains': 'انبار:',
      id: 'شناسه مرسوله:',
      'id.equals': 'شناسه مرسوله:',
      'id.contains': 'شناسه مرسوله:',
      orderId: 'شناسه سفارش:',
      'orderId.equals': 'شناسه سفارش:',
      'orderId.contains': 'شناسه سفارش:',
      deliveryDate: 'تاریخ تحویل',
      'deliveryDate.equals': 'تاریخ تحویل:',
      'deliveryDate.greaterThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.greaterThan': 'تاریخ تحویل:',
      'deliveryDate.lessThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.lessThan': 'تاریخ تحویل:',
      packagedDate: 'تاریخ تحویل به لجستیک:',
      'packagedDate.equals': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThan': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThan': 'تاریخ تحویل به لجستیک:',
      status: 'وضعیت سفارش:',
      'status.in': 'وضعیت سفارش:',
      'status.notIn': 'وضعیت سفارش:',
      partnerName: 'شرکت:',
      'partnerName.equals': 'شرکت:',
      'partnerName.contains': 'شرکت:',
      weight: 'وزن:',
      'weight.equals': 'وزن:',
      'weight.contains': 'وزن:',
      length: 'ابعاد:',
      'length.equals': 'ابعاد:',
      'length.contains': 'ابعاد:',
    };
    return status[fieldName];
  };
  const removeFilterHandler = (fieldName: any) => {
    const filtered = { ...filtersValues };
    delete filtered[fieldName];
    setFiltersValues(filtered);
  };
  const actionRetrieveRubikaOrders = async (action: any) => {
    const payload = { ...action };
    try {
      setLoading(true);
      const response = await actionRepository.actionRetrieveRubikaOrders({
        payload,
      });
      response.data.length === 0
        ? handleNotfication('سفارش جدیدی اضافه نشده است', 'warn')
        : handleNotfication('سفارش جدیدی اضافه شد', 'success');
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const updateOrderStatus = async (action: any) => {
    const payload = { ...action };
    try {
      setLoading(true);
      const { pagination, callback } = await ordersRepository.updateOrderStatus({
        payload,
      });
      callback && callback();
      handleNotfication('تغییر وضعیت سفارش با موفقیت انجام شد', 'success');
    } catch (error) {
      handleNotfication('تغییر وضعیت سفارش انجام نشد', 'error');
    } finally {
      setLoading(false);
    }
  };
  const allOrder = async (action: any) => {
    const payload = { ...action };
    try {
      setLoading(true);
      const response = await ordersRepository.getAllOrdersReport({ payload });
      setAllOrders(response.data);
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  // Bad Bad Bad.. :(
  ordersList[0]?.created &&
    ordersList[0]?.created > lastUpdate &&
    setLastUpdate(ordersList[0]?.created);

  useEffect(() => {
    getOrders();
    setupFilters();
  }, [filtersValues]);

  useEffect(() => {
    if (width && width <= 480) {
      setFiltersValues({ ...filtersValues, 'operatorId.equals': userId })
      setIsMob(true);
    } else {
      setIsMob(false);
    }
  }, [width]);

  const classes = useStyles();
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({ ...filtersValues, page: page - 1 });
  };
  const getorder = async (action: any) => {
    const payload = { ...action };
    try {
      setLoading(true);
      const { response, count } = await ordersRepository.getOrders({
        payload,
      });
      setCount(count.data);
      setResponse(response.data);
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const getOrders = async () => {
    const { page, size } = filtersValues;
    getorder({
      ...filtersValues,
      page,
      size,
    });
    // const { response, count } = await Repository.warehouseOperatorOrders({
    //     ...filtersValues,
    //     page,
    //     size
    // });
  };

  const setupFilters = () => {
    setFilters([
      {
        type: 'input',
        id: 'customerName.contains',
        label: 'نام تحویل گیرنده',
        icon: mdiAccountOutline,
      },
      {
        type: 'input',
        id: 'customerMobileNumber.contains',
        label: 'شماره همراه تحویل گیرنده',
        icon: mdiPhoneOutline,
      },
      {
        type: 'input',
        id: 'id.equals',
        label: 'شناسه سفارش',
        icon: mdiPackageVariant,
      },
      {
        type: 'checkbox',
        id: 'status.in',
        label: 'وضعیت سفارش',
        icon: mdiStateMachine,
        data: ['ASSIGNED', 'PICKED', 'PACKAGED'],
        translateType: 'Order',
      },
      {
        type: 'select',
        id: 'deliveryStartTime.equals',
        label: 'بازه تحویل',
        data: ['09:00:00', '15:00:00'],
        translateType: 'Order',
        icon: mdiClock,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.greaterThanOrEqual',
        label: 'تاریخ تحویل از ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.lessThanOrEqual',
        label: 'تا تاریخ تحویل ',
        icon: mdiCalendar,
      },
      {
        type: 'checkbox',
        id: 'paymentType.in',
        label: 'شیوه پرداخت',
        icon: mdiStateMachine,
        data: ['COD', 'ONLINE'],
        translateType: 'COD',
      },
    ]);
  };
  let headCells = [];
  headCells = [
    { id: 'partnerName', label: 'نام شرکت', type: 'input' },
    {
      id: 'customerName',
      label: 'سفارش‌دهنده',
      type: 'input',
    },
    {
      id: 'id',
      label: 'شناسه سفارش',
      type: 'input',
    },
    {
      id: 'items',
      label: 'تعداد آیتم‌های سفارش',
      type: 'input',
    },
    {
      id: 'deliveryDate',
      label: 'تاریخ تحویل',
      type: 'date-picker',
    },
    { id: 'trackingCode', label: 'کد رهگیری', type: 'input' },
    {
      type: 'checkbox',
      id: 'deliveryType',
      label: 'نوع پردازش',
      data: ['COURIER', 'POST', 'EXPRESS_POST', 'SPECIAL_POST', 'VIP'],
      translateType: 'SendDeliveryType',
    },
    {
      type: 'checkbox',
      id: 'orderTplType',
      label: 'ناوگان حمل',
      data: [
        "NONE",
        "LINK_EXPRESS",
        "ALOPEYK",
        "TAROFF",
        "CHAPAR",
        "PETROEX",
        "SPEED",
        "DTSXP",
        "TAPIN_POST",
        "TAPIN_KETAB",
        "NEDEX",
      ],
      translateType: 'TPL',
    },
    {
      type: 'checkbox',
      id: 'paymentType',
      label: 'شیوه پرداخت',
      data: ['COD', 'ONLINE'],
      translateType: 'COD',
    },
    {
      type: 'checkbox',
      id: 'status',
      label: 'وضعیت',
      data: ['ASSIGNED', 'PICKED', 'PACKAGED'],
      translateType: 'Order',
    },
  ];
  const head = { headCells };
  const cancelOrder = async (action: any) => {
    const payload = { ...action };
    try {
      setLoading(true);
      const pagination = await ordersRepository.cancelOrder({ payload });
      handleNotfication('سفارش مورد نظر شمابا موفقیت لغو شد', 'success');
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    await cancelOrder({
      id,
      body: { description: cancelDescription },
    });
    getOrders();
    setOpenConfirmCancel(false);
  };

  const changeStatusOrder = () => {
    updateOrderStatus({
      id,
      body: previewState,
      pagination: filtersValues,
    });
    setOpenConfirmUpdateState(false);
  };
  const updateAllOrderStatus = async (action: any) => {
    const payload = { ...action };
    try {
      setLoading(true);
      const pagination = await ordersRepository.updateAllOrderStatus({ payload });
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const changeStatusOrderWhitId = (data: any) => {
    const { ids, status, description } = data;
    updateAllOrderStatus({
      ids,
      status,
      description,
      pagination: filtersValues,
    });
    setOpenConfirmUpdateState(false);
  };

  const handleOpenConfirmUpdateState = (data: { id: number; body: Status }) => {
    setOpenConfirmUpdateState(true);
    setId(data.id);
    setPreviewState(data.body);
  };

  const handleClose = () => {
    setOpenConfirmCancel(false);
    setOpenConfirmUpdateState(false);
    setOpenSize(false);
  };

  const findOrderStatus = (id: number) => {
    const status = ordersList.find(order => order.id === id)?.status;
    return status && translate(status, 'order');
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFiltersValues({
      ...filtersValues,
      page: 0,
      size: event.target.value,
    });
  };

  const SubmitHanler = async (data: any) => {
    if (data.idType?.length < 10) {
      searchWithId(data)
    } else {
      serialSubmitHanler(data)
    }
  }

  const searchWithId = async (data: any) => {
    const payload = {
      id: data.idType
    };
    try {
      setLoading(true);
      const response = await ordersRepository.getOrderWithId(
        { payload }
      );
      switch (response.data.status) {
        case 'ASSIGNED':
          props.history.push(`/warehouse-operator-orders/${response.data.id}/pick`)
          break;
        case 'PICKED':
          props.history.push(`/warehouse-operator-orders/${response.data.id}/pack`)
          break;

      }
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  }

  const serialSubmitHanler = async (data: any) => {
    const payload = {
      "serialNumber.equals": data.idType
    };
    try {
      setLoading(true);
      const { response } = await inventoryItemsRepository.getAllInventoryItems(
        { payload }
      );
      const item = response.data.content[0]
      if (item) {
        switch (item.orderStatus) {
          case 'ASSIGNED':
            props.history.push(`/warehouse-operator-orders/${item.orderId}/pick`)
            break;
          case 'PICKED':
            props.history.push(`/warehouse-operator-orders/${item.orderId}/pack`)
            break;
        }
      }
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthConsumer>
      {({ user }) => (

        <div className={classes.root}>
          <Grid container spacing={5}>
            <Grid container item xs={12} sm={6} alignItems="center">
              <Typography
                variant="h4"
                component="div"
                style={{ marginLeft: '20px', fontSize: '18px' }}
              >
                {'لیست سفارش‌ها'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <RenderFilter
                filters={filters}
                onFilter={(filtervalues: any) => {
                  setFiltersValues({
                    ...removeEmptyValueObject(filtervalues),
                    page: 0,
                    size: 20,
                    'status.in': 'ASSIGNED, PICKED, PACKAGED',
                    'enableGetChildren.equals': true,
                  });
                }}
                mainFiltersCount={2}
                baseFilter={{
                  type: 'input',
                  id: 'id.equals',
                  placeholder: 'شناسه سفارش',
                }}
                filterValues={filtersValues}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <form
                style={{ width: '320px', textAlign: 'center', margin: '50px 40px' }}
                onSubmit={handleSubmit(SubmitHanler)}
              >
                <Grid
                  className={classes.secondDetail}
                  container
                  spacing={2}
                  alignItems="center"
                >
                  <Grid item xs={9} alignItems="center">
                    {/* <span >
                      <Icon path={mdiBarcodeScan} size={1} color="black" />
                    </span> */}
                    <span>
                      <TextField
                        fullWidth
                        id="idType"
                        name="idType"
                        size="small"
                        inputRef={register({
                          required: true,
                        })}
                        autoFocus
                        label="شناسه سفارش"
                      />
                    </span>
                  </Grid>
                  <Grid item xs={3}>
                    <IconButton
                      aria-label="details"
                      type="submit"
                      size="small"
                      title="ادامه پروسه"
                      data-cy="details"
                    >
                      <Icon
                        path={mdiPackageVariant}
                        size={1}
                        color={theme.palette.info.main}
                      />
                    </IconButton>
                  </Grid>
                </Grid>
              </form>
            </Grid>
            <Grid
              container
              item
              xs={12}
              md={6}
              direction="row-reverse"
              alignItems="center"
            >
              <div className={classes.btnBox}>
                <Can
                  role={user.role}
                  perform="orders:btnGetNewOrders"
                  yes={() => (
                    <>
                      <Button
                        variant="contained"
                        color="secondary"
                        size={
                          checkMatchMedia('(max-width: 1024px)')
                            ? 'small'
                            : 'large'
                        }
                        onClick={() => actionRetrieveRubikaOrders({})}
                      >
                        {'دریافت سفارش جدید'}
                      </Button>
                    </>
                  )}
                />

                <Can
                  role={user.role}
                  perform="orders:downloadReportCSV"
                  yes={() => (
                    <>
                      <CSV
                        ordersDispatch={allOrder}
                        data={allOrders}
                        filtersValues={filtersValues}
                        loading={loading}
                      />
                    </>
                  )}
                />
              </div>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {loading ? (
                <div className={classes.loading}>
                  <CircularProgress />
                </div>
              ) : (
                <>
                  {ordersList.length > 0 ? (
                    <List
                      data={ordersList}
                      changeStatusOrderWhitId={changeStatusOrderWhitId}
                      handleOpenConfirmUpdateState={
                        handleOpenConfirmUpdateState
                      }
                      head={head}
                      filterVal={filtersValues}
                      onFilterValue={(filtervalues: any) => {
                        setFiltersValues({
                          ...removeEmptyValueObject(filtervalues),
                          ...filtersValues,
                          page: 0,
                          size: 10,
                        });
                      }}
                      removeFilterHandler={removeFilterHandler}
                      filterLabelTranslator={filterTranslator}
                    />
                  ) : (
                    <Typography variant="h3" component="div">
                      سفارشی موجود نیست
                    </Typography>
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {ordersList.length > 0 && (
                <FormControl
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      marginLeft: '10px',
                      color: '#8a8a8a',
                    }}
                  >
                    {' '}
                    تعداد نمایش در هر صفحه:
                  </div>
                  <Select
                    open={openSize}
                    onClose={handleClose}
                    onOpen={() => setOpenSize(true)}
                    value={filtersValues.size}
                    onChange={handleChangeSizePage}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid xs={12} sm={6} container item direction="row-reverse">
              {ordersList.length > 0 && (
                <Pagination
                  className={classes.pagination}
                  count={Math.ceil(count / filtersValues.size)}
                  color="secondary"
                  page={filtersValues.page + 1}
                  onChange={handleChangePage}
                />
              )}
            </Grid>
          </Grid>

          <Dialog
            open={openConfirmCancel}
            fullWidth
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                {handleClose ? (
                  <IconButton
                    aria-label="close"
                    // className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <Icon path={mdiClose} size={1} />
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.cancelIcon}>
                <Icon
                  path={mdiClose}
                  size={4}
                  color={theme.palette.error.main}
                />
              </div>
              <div
                className={classes.cancelTitle}
              >{`از لغو سفارش با شناسه ${id} مطمئن هستید؟`}</div>
              <TextField
                className={classes.textArea}
                placeholder={'علت لغو...'}
                value={cancelDescription}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCancelDescription(e.target.value)
                }
              />
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {'خیر'}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCancelOrder}
                    disabled={!(cancelDescription.length > 0)}
                    size="large"
                  >
                    {'بله'}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>

          <Dialog
            fullWidth
            open={openConfirmUpdateState}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                {handleClose ? (
                  <IconButton
                    aria-label="close"
                    // className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <Icon path={mdiClose} size={1} />
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.editIcon}>
                <Icon
                  path={mdiPencilOutline}
                  size={4}
                  color={theme.palette.blue.main}
                />
              </div>
              <div className={classes.cancelTitle}>
                {`از تغییر تگ وضعیت سفارش
                با شناسه  ${id} از
 "${id > -1 && findOrderStatus(id)}"
 به
" ${translate(previewState.replace(/"/g, '') as Status, 'order')}"

 
 
 مطمئن هستید؟`}
              </div>
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {'خیر'}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={changeStatusOrder}
                    size="large"
                  >
                    {'بله'}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </AuthConsumer>
  );
};

export default Orders;
